import React, { useState } from "react";
import { Modal, Button, Form as BtForm, InputGroup, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { IoMdAddCircle } from "react-icons/io";
import { LuBriefcase, LuMail, LuPhone, LuMapPin, LuArrowRight, LuSparkles } from "react-icons/lu";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createBusiness, getBusinessesList } from "../../../redux/reducers/businessSlice";
import { getProfile } from "../../../redux/reducers/authSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function AddBusiness() {
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const { businessList, loading: businessLoading } = useSelector((state) => state.business);
  const { userDetail } = useSelector((state) => state.auth);
  const masterCustomerId = localStorage.getItem("masterCustomerId") || userDetail?.id || userDetail?._id;

  const isFreeAccount = userDetail?.subscriptionTier === 'free';
  const hasReachedLimit = isFreeAccount && businessList?.length >= 1;

  const validationSchema = Yup.object({
    name: Yup.string().required("Business Name is required"),
    businessType: Yup.string().oneOf(
      ['barber', 'spa', 'nail-arts', 'tatoo', 'beauty-salon', 'other'],
      "Invalid Business Type"
    ).required("Required"),
    location: Yup.string().required("Address is required"),
    phone: Yup.string().required("Phone is required"),
    businessEmail: Yup.string().email("Invalid email").required("Required"),
    countryOfOperation: Yup.string().oneOf(['canada', 'usa', 'mexico'], "Invalid Country").required("Required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const generateSlug = (text) => {
      return text.toLowerCase().trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

    const payload = {
      name: values.name,
      slug: `${generateSlug(values.name)}-${Math.floor(1000 + Math.random() * 9000)}`,
      businessType: values.businessType,
      logo: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=200",
      location: values.location,
      contact: { email: values.businessEmail, phone: values.phone },
      countryOfOperation: values.countryOfOperation,
      masterCustomer: masterCustomerId
    };

    try {
      const resultAction = await dispatch(createBusiness(payload)).unwrap();
      setShow(false);
      dispatch(getProfile());
      if (masterCustomerId) {
        dispatch(getBusinessesList(masterCustomerId));
      }
      navigate(`/business-detail/${resultAction?._id || resultAction?.id}`);
    } catch (err) {
      console.error("Create Business Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Free tier is limited to 1 business. Upgrade to Premium for unlimited!
    </Tooltip>
  );

  return (
    <div>
      {hasReachedLimit ? (
        <OverlayTrigger placement="bottom" overlay={renderTooltip}>
          <span className="d-inline-block">
            <button className="btn btn-primary mx-2 opacity-50 cursor-not-allowed d-flex align-items-center gap-2" disabled>
              <IoMdAddCircle size={18} /> New Business
            </button>
          </span>
        </OverlayTrigger>
      ) : (
        <div className="btn btn-primary mx-2 d-flex align-items-center gap-2" onClick={handleShow}>
          <IoMdAddCircle size={18} /> New Business
        </div>
      )}

      <Modal show={show} onHide={handleClose} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">New Business</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Formik
            initialValues={{ name: "", businessType: "", location: "", phone: "", businessEmail: "", countryOfOperation: "canada" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form>
                <BtForm.Group className="mb-3">
                  <BtForm.Label className="small fw-bold">Business Name</BtForm.Label>
                  <InputGroup className="bg-light rounded-3 border">
                    <InputGroup.Text className="bg-transparent border-0 text-muted"><LuBriefcase size={18} /></InputGroup.Text>
                    <BtForm.Control name="name" placeholder="The Royal Cut" className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('name')} />
                  </InputGroup>
                  <ErrorMessage name="name" component="div" className="text-danger x-small mt-1" />
                </BtForm.Group>

                <BtForm.Group className="mb-3">
                  <BtForm.Label className="small fw-bold">Business Type</BtForm.Label>
                  <BtForm.Select className="bg-light border shadow-none py-2 rounded-3" {...formik.getFieldProps('businessType')}>
                    <option value="">Select Type</option>
                    <option value="barber">Barbershop</option>
                    <option value="spa">Spa</option>
                    <option value="nail-arts">Nail Arts</option>
                    <option value="tatoo">Tattoo Parlor</option>
                    <option value="beauty-salon">Beauty Salon</option>
                    <option value="other">Other</option>
                  </BtForm.Select>
                  <ErrorMessage name="businessType" component="div" className="text-danger x-small mt-1" />
                </BtForm.Group>

                <div className="row">
                  <div className="col-md-6">
                    <BtForm.Group className="mb-3">
                      <BtForm.Label className="small fw-bold">Business Email</BtForm.Label>
                      <InputGroup className="bg-light rounded-3 border">
                        <InputGroup.Text className="bg-transparent border-0 text-muted"><LuMail size={18} /></InputGroup.Text>
                        <BtForm.Control name="businessEmail" placeholder="info@biz.com" className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('businessEmail')} />
                      </InputGroup>
                      <ErrorMessage name="businessEmail" component="div" className="text-danger x-small mt-1" />
                    </BtForm.Group>
                  </div>
                  <div className="col-md-6">
                    <BtForm.Group className="mb-3">
                      <BtForm.Label className="small fw-bold">Phone</BtForm.Label>
                      <InputGroup className="bg-light rounded-3 border">
                        <InputGroup.Text className="bg-transparent border-0 text-muted"><LuPhone size={18} /></InputGroup.Text>
                        <BtForm.Control name="phone" placeholder="+1..." className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('phone')} />
                      </InputGroup>
                      <ErrorMessage name="phone" component="div" className="text-danger x-small mt-1" />
                    </BtForm.Group>
                  </div>
                </div>

                <BtForm.Group className="mb-3">
                  <BtForm.Label className="small fw-bold">Location Address</BtForm.Label>
                  <InputGroup className="bg-light rounded-3 border">
                    <InputGroup.Text className="bg-transparent border-0 text-muted"><LuMapPin size={18} /></InputGroup.Text>
                    <BtForm.Control name="location" placeholder="123 Street..." className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('location')} />
                  </InputGroup>
                  <ErrorMessage name="location" component="div" className="text-danger x-small mt-1" />
                </BtForm.Group>

                <BtForm.Group className="mb-4">
                  <BtForm.Label className="small fw-bold">Country of Operation</BtForm.Label>
                  <BtForm.Select className="bg-light border shadow-none py-2 rounded-3" {...formik.getFieldProps('countryOfOperation')}>
                    <option value="canada">Canada</option>
                    <option value="usa">USA</option>
                    <option value="mexico">Mexico</option>
                  </BtForm.Select>
                  <ErrorMessage name="countryOfOperation" component="div" className="text-danger x-small mt-1" />
                </BtForm.Group>

                <Button variant="brand" type="submit" className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2" disabled={formik.isSubmitting || businessLoading} style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}>
                  {formik.isSubmitting || businessLoading ? <Spinner size="sm" /> : "CREATE BUSINESS"}
                  <LuArrowRight size={18} />
                </Button>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </div>
  );
}
